import React, { useMemo, useState, useEffect } from 'react';
import {
    Modal,
    Timeline,
    Typography,
    Tag,
    Empty,
    Space,
    Tooltip,
    Input,
    Button,
    Popconfirm,
    Popover,
    Spin,
    message
} from 'antd';
import {
    ShopOutlined,
    SwapOutlined,
    UserOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    FileTextOutlined,
    InfoCircleOutlined,
    DollarOutlined,
    PhoneOutlined,
    EnvironmentOutlined,
    IdcardOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import API from '../../API/API';

const { Text, Paragraph } = Typography;

/**
 * Read + notes-CRUD viewer for a lead's `yardReassignmentHistory`.
 * Shared by every page that exposes "Re-assign Yard": Order Process,
 * Order Complete, ReLocate and Lead Details.
 *
 * "History Notes" are free-form follow-up notes attached to a single history
 * entry AFTER the reassignment. They are distinct from the entry's `comment`
 * (captured once, at reassignment time) and are managed through dedicated
 * endpoints, so editing them never touches the yard array.
 *
 * Props:
 *  - open / visible : boolean
 *  - onClose        : () => void
 *  - history        : yardReassignmentHistory array
 *  - leadId         : lead _id (required for notes CRUD)
 *  - yardDetailsMap : seed { yardId: { yardName, ... } } for the "View Yard Details"
 *                     popover; any missing yard is lazily fetched on first open
 *  - leadName, orderNumber : header context
 *  - onNotesChanged : optional () => void, fired after a successful note
 *                     add/edit/delete so the parent can refresh its row
 */

const getCurrentUser = () => {
    try {
        return JSON.parse(localStorage.getItem('userData') || '{}') || {};
    } catch {
        return {};
    }
};

const normId = (value) => {
    if (!value) return '';
    if (typeof value === 'object') return String(value._id || value.id || '');
    return String(value);
};

const firstOf = (value) => {
    if (Array.isArray(value)) {
        const found = value.find((v) => v && String(v).trim() !== '');
        return found ? String(found).trim() : '';
    }
    return value != null && String(value).trim() !== '' ? String(value).trim() : '';
};

const formatYardAddress = (addr) => {
    if (!addr) return '';
    if (typeof addr === 'string') return addr;
    return [addr.address, addr.street, addr.city, addr.state, addr.country, addr.zip_code]
        .filter((part) => part && String(part).trim() !== '')
        .join(', ');
};

const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

// The per-assignment price/contact/address the "View Yard Details" popover needs
// lives on the lead's `yard[]` entries (NOT the Yard master record that
// yardDetailsMap holds). Pick the most recent assignment for a given yardId.
const pickAssignmentForYard = (leadYards, yardId) => {
    const id = normId(yardId);
    if (!id) return null;

    const matches = (Array.isArray(leadYards) ? leadYards : [])
        .filter((y) => normId(y?.yardId) === id);

    if (matches.length === 0) return null;

    return [...matches].sort(
        (a, b) => new Date(b?.yardConfirmDate || 0) - new Date(a?.yardConfirmDate || 0)
    )[0];
};

const resolveActorName = (entry = {}, nameKey = 'changedByName', emailKey = 'changedByEmail', refKey = 'changedBy') => {
    if (entry[nameKey]) return entry[nameKey];

    const actor = entry[refKey];
    if (actor && typeof actor === 'object') {
        const fullName = [actor.firstName, actor.lastName].filter(Boolean).join(' ').trim();
        if (fullName) return fullName;
        if (actor.userName) return actor.userName;
        if (actor.name) return actor.name;
    }

    return entry[emailKey] || 'Unknown user';
};

const resolveYardName = (yardId, snapshotName, yardDetailsMap = {}) => {
    if (snapshotName) return snapshotName;
    const id = normId(yardId);
    if (!id) return '';
    const details = yardDetailsMap[id] || yardDetailsMap[String(id)];
    return details?.yardName || details?.name || id;
};

const noteAuthorId = (note = {}) => {
    const ref = note.createdBy;
    if (ref && typeof ref === 'object') return String(ref._id || ref.id || '');
    return ref ? String(ref) : '';
};

const YardReassignmentHistoryModal = ({
    open,
    visible,
    onClose,
    history,
    leadId,
    yards,
    yardDetailsMap = {},
    leadName,
    orderNumber,
    onNotesChanged
}) => {
    const isOpen = open ?? visible ?? false;

    const [localHistory, setLocalHistory] = useState(Array.isArray(history) ? history : []);
    const [noteDrafts, setNoteDrafts] = useState({});          // { [historyId]: text }
    const [editing, setEditing] = useState(null);              // { historyId, noteId, text }
    const [busyKey, setBusyKey] = useState(null);              // disables buttons while a request runs

    // "View Yard Details" popover — lazily fetched yard master records.
    const [yardCache, setYardCache] = useState({});
    const [yardLoading, setYardLoading] = useState({});
    const mergedYardMap = useMemo(
        () => ({ ...(yardDetailsMap || {}), ...yardCache }),
        [yardDetailsMap, yardCache]
    );

    // The lead's yard[] assignments (per-assignment price/contact/address). Seeded
    // from the `yards` prop; falls back to fetching the lead when only `leadId` is
    // available so the price matches what the parent table shows.
    const [leadYards, setLeadYards] = useState(Array.isArray(yards) ? yards : []);

    useEffect(() => {
        if (Array.isArray(yards) && yards.length > 0) {
            setLeadYards(yards);
        }
    }, [yards]);

    useEffect(() => {
        if (!isOpen) return undefined;
        if (Array.isArray(yards) && yards.length > 0) return undefined;
        if (!leadId) return undefined;

        let cancelled = false;
        (async () => {
            try {
                const res = await API.GetLeadById(leadId);
                const data = res?.data || null;
                if (!cancelled && data && Array.isArray(data.yard)) {
                    setLeadYards(data.yard);
                }
            } catch (error) {
                console.error('Failed to load lead yards for history modal:', error);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen, leadId, yards]);

    const currentUser = useMemo(getCurrentUser, [isOpen]);
    const currentUserId = String(currentUser?._id || currentUser?.id || '');
    const isPrivileged = ['ADMIN', 'MANAGER'].includes(currentUser?.role);

    // Keep the local copy in sync with the prop whenever it changes / modal reopens.
    useEffect(() => {
        setLocalHistory(Array.isArray(history) ? history : []);
    }, [history, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setNoteDrafts({});
            setEditing(null);
            setBusyKey(null);
        }
    }, [isOpen]);

    const ensureYardDetails = async (yardId) => {
        const id = normId(yardId);
        if (!id || mergedYardMap[id] || yardLoading[id]) return;

        setYardLoading((prev) => ({ ...prev, [id]: true }));
        try {
            const res = await API.GetYardById(id);
            const data = res?.data || res || null;
            if (data && typeof data === 'object') {
                setYardCache((prev) => ({ ...prev, [id]: data }));
            }
        } catch (error) {
            console.error('Failed to load yard details:', error);
        } finally {
            setYardLoading((prev) => ({ ...prev, [id]: false }));
        }
    };

    const sortedHistory = useMemo(() => {
        const items = Array.isArray(localHistory) ? localHistory.filter(Boolean) : [];
        return [...items].sort(
            (a, b) => new Date(b.changedAt || 0) - new Date(a.changedAt || 0)
        );
    }, [localHistory]);

    const applyResponse = (response, successMsg) => {
        if (response?.status && response?.data?.yardReassignmentHistory) {
            setLocalHistory(response.data.yardReassignmentHistory);
            message.success(response.message || successMsg);
            if (typeof onNotesChanged === 'function') onNotesChanged();
            return true;
        }
        message.error(response?.message || 'Something went wrong');
        return false;
    };

    const handleAddNote = async (historyId) => {
        const text = (noteDrafts[historyId] || '').trim();
        if (!text) {
            message.warning('Please enter a note');
            return;
        }
        setBusyKey(`add-${historyId}`);
        try {
            const response = await API.addYardReassignmentNote(leadId, historyId, text);
            if (applyResponse(response, 'Note added')) {
                setNoteDrafts((prev) => ({ ...prev, [historyId]: '' }));
            }
        } catch (error) {
            console.error('Error adding note:', error);
            message.error('Failed to add note');
        } finally {
            setBusyKey(null);
        }
    };

    const handleSaveEdit = async () => {
        if (!editing) return;
        const text = (editing.text || '').trim();
        if (!text) {
            message.warning('Note cannot be empty');
            return;
        }
        setBusyKey(`edit-${editing.noteId}`);
        try {
            const response = await API.updateYardReassignmentNote(leadId, editing.historyId, editing.noteId, text);
            if (applyResponse(response, 'Note updated')) {
                setEditing(null);
            }
        } catch (error) {
            console.error('Error updating note:', error);
            message.error('Failed to update note');
        } finally {
            setBusyKey(null);
        }
    };

    const handleDeleteNote = async (historyId, noteId) => {
        setBusyKey(`delete-${noteId}`);
        try {
            const response = await API.deleteYardReassignmentNote(leadId, historyId, noteId);
            applyResponse(response, 'Note deleted');
        } catch (error) {
            console.error('Error deleting note:', error);
            message.error('Failed to delete note');
        } finally {
            setBusyKey(null);
        }
    };

    const canModifyNote = (note) => isPrivileged || (currentUserId && noteAuthorId(note) === currentUserId);

    // Info icon + popover with the yard details, next to a yard name.
    // Price / contact / address come from the lead's per-assignment yard[] entry
    // (the same source the parent table's "Price: $x" uses); the Yard master record
    // (yardDetailsMap / lazy fetch) is only a fallback for contact + address.
    const renderYardDetailsPopover = (yardId, snapshotName) => {
        const id = normId(yardId);
        if (!id) return null;

        const details = mergedYardMap[id];
        const assignment = pickAssignmentForYard(leadYards, id);
        const loading = !!yardLoading[id];

        const priceValue = assignment
            ? (assignment.finalPrice || assignment.price || assignment.originalPrice || 0)
            : null;
        const contactPerson = firstOf(assignment?.contactPerson) || firstOf(details?.contactPerson);
        const contactPhone = firstOf(assignment?.contactNumber) || firstOf(details?.contactNumbers);
        const address = formatYardAddress(assignment?.yardAddress) || formatYardAddress(details?.yardAddress);
        const hasAnyDetail = assignment || details;

        const row = (label, value) => (
            <div style={{ display: 'flex', gap: 6 }}>
                <Text type="secondary" style={{ minWidth: 62 }}>{label}:</Text>
                <Text style={{ flex: 1, wordBreak: 'break-word' }}>{value || 'N/A'}</Text>
            </div>
        );

        const content = (
            <div style={{ width: 260, fontSize: 12, lineHeight: 1.8 }}>
                {loading && !hasAnyDetail ? (
                    <div style={{ textAlign: 'center', padding: '8px 0' }}>
                        <Spin size="small" />
                    </div>
                ) : (
                    <>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <Text type="secondary" style={{ minWidth: 62 }}>
                                <IdcardOutlined /> Yard ID:
                            </Text>
                            <Text copyable={{ text: id }} style={{ flex: 1, fontSize: 11 }}>{id}</Text>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <Text type="secondary" style={{ minWidth: 62 }}>
                                <DollarOutlined /> Price:
                            </Text>
                            <Text style={{ flex: 1 }}>
                                {priceValue != null ? formatMoney(priceValue) : 'N/A'}
                            </Text>
                        </div>
                        {row(<><UserOutlined /> Contact</>, contactPerson)}
                        {row(<><PhoneOutlined /> Phone</>, contactPhone)}
                        {row(<><EnvironmentOutlined /> Address</>, address)}
                        {!hasAnyDetail && (
                            <div style={{ marginTop: 6 }}>
                                <Text type="secondary">Some details not available</Text>
                            </div>
                        )}
                    </>
                )}
            </div>
        );

        return (
            <Popover
                title={
                    <Space>
                        <ShopOutlined style={{ color: '#722ed1' }} />
                        {resolveYardName(id, snapshotName || details?.yardName, mergedYardMap)}
                    </Space>
                }
                content={content}
                trigger="click"
                placement="rightTop"
                onOpenChange={(nextOpen) => {
                    if (nextOpen) ensureYardDetails(id);
                }}
            >
                <Tooltip title="View yard details">
                    <Button
                        type="link"
                        size="small"
                        icon={<InfoCircleOutlined />}
                        style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                        aria-label="View yard details"
                    />
                </Tooltip>
            </Popover>
        );
    };

    const renderNotes = (entry) => {
        const historyId = entry._id;
        const notes = Array.isArray(entry.notes) ? entry.notes : [];

        return (
            <div
                style={{
                    marginTop: 10,
                    padding: '8px 10px',
                    background: '#f0f5ff',
                    borderRadius: 8
                }}
            >
                <Text strong style={{ fontSize: 12 }}>
                    <FileTextOutlined /> History Notes ({notes.length})
                </Text>

                {notes.length > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {notes.map((note) => {
                            const isEditingThis = editing
                                && editing.historyId === historyId
                                && editing.noteId === note._id;

                            return (
                                <div
                                    key={note._id}
                                    style={{
                                        background: '#ffffff',
                                        border: '1px solid #d6e4ff',
                                        borderRadius: 6,
                                        padding: '6px 8px'
                                    }}
                                >
                                    {isEditingThis ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            <Input.TextArea
                                                value={editing.text}
                                                onChange={(e) => setEditing((prev) => ({ ...prev, text: e.target.value }))}
                                                rows={2}
                                                maxLength={500}
                                                showCount
                                                autoFocus
                                            />
                                            <Space size={6}>
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    loading={busyKey === `edit-${note._id}`}
                                                    onClick={handleSaveEdit}
                                                >
                                                    Save
                                                </Button>
                                                <Button size="small" onClick={() => setEditing(null)}>
                                                    Cancel
                                                </Button>
                                            </Space>
                                        </div>
                                    ) : (
                                        <>
                                            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {note.text}
                                            </div>
                                            <div
                                                style={{
                                                    marginTop: 4,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    gap: 8
                                                }}
                                            >
                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                    {resolveActorName(note, 'createdByName', 'createdByEmail', 'createdBy')}
                                                    {' · '}
                                                    {note.createdAt ? dayjs(note.createdAt).format('MMM DD, YYYY hh:mm A') : ''}
                                                    {note.updatedAt && note.createdAt
                                                        && dayjs(note.updatedAt).diff(dayjs(note.createdAt), 'second') > 1
                                                        ? ' (edited)'
                                                        : ''}
                                                </Text>
                                                {canModifyNote(note) && (
                                                    <Space size={2}>
                                                        <Tooltip title="Edit note">
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<EditOutlined />}
                                                                onClick={() => setEditing({
                                                                    historyId,
                                                                    noteId: note._id,
                                                                    text: note.text
                                                                })}
                                                            />
                                                        </Tooltip>
                                                        <Popconfirm
                                                            title="Delete this note?"
                                                            okText="Delete"
                                                            okButtonProps={{ danger: true, loading: busyKey === `delete-${note._id}` }}
                                                            onConfirm={() => handleDeleteNote(historyId, note._id)}
                                                        >
                                                            <Tooltip title="Delete note">
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    danger
                                                                    icon={<DeleteOutlined />}
                                                                />
                                                            </Tooltip>
                                                        </Popconfirm>
                                                    </Space>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {leadId && (
                    <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                        <Input.TextArea
                            placeholder="Add a note to this reassignment…"
                            value={noteDrafts[historyId] || ''}
                            onChange={(e) => setNoteDrafts((prev) => ({ ...prev, [historyId]: e.target.value }))}
                            rows={1}
                            maxLength={500}
                            autoSize={{ minRows: 1, maxRows: 4 }}
                        />
                        <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            loading={busyKey === `add-${historyId}`}
                            onClick={() => handleAddNote(historyId)}
                        >
                            Add
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Modal
            title={
                <Space>
                    <SwapOutlined style={{ color: '#722ed1' }} />
                    <span>Yard Reassignment History</span>
                    {orderNumber ? <Tag color="purple">{orderNumber}</Tag> : null}
                </Space>
            }
            open={isOpen}
            onCancel={onClose}
            footer={null}
            width={680}
            centered
        >
            {leadName ? (
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    {leadName}
                </Text>
            ) : null}

            {sortedHistory.length === 0 ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No yard reassignments recorded yet"
                />
            ) : (
                <Timeline
                    mode="left"
                    items={sortedHistory.map((entry, index) => {
                        const isInitial = !entry.previousYardId;
                        const isLatest = index === 0;
                        const previousName = resolveYardName(
                            entry.previousYardId,
                            entry.previousYardName,
                            mergedYardMap
                        );
                        const newName = resolveYardName(
                            entry.newYardId,
                            entry.newYardName,
                            mergedYardMap
                        );
                        const actorName = resolveActorName(entry);
                        const when = entry.changedAt
                            ? dayjs(entry.changedAt).format('MMM DD, YYYY hh:mm A')
                            : 'Date not available';

                        return {
                            key: entry._id || index,
                            color: isLatest ? 'green' : 'blue',
                            dot: isLatest ? <CheckCircleOutlined /> : <ShopOutlined />,
                            children: (
                                <div style={{ paddingBottom: 8 }}>
                                    <Space wrap size={6} style={{ marginBottom: 4 }}>
                                        {isInitial ? (
                                            <Tag color="blue">Yard assigned</Tag>
                                        ) : (
                                            <Tag color="purple">Yard reassigned</Tag>
                                        )}
                                        {isLatest ? <Tag color="green">Current</Tag> : null}
                                    </Space>

                                    <div style={{ fontSize: 14, marginBottom: 4 }}>
                                        {isInitial ? (
                                            <Space size={4}>
                                                <Text strong>{newName || 'N/A'}</Text>
                                                {renderYardDetailsPopover(entry.newYardId, entry.newYardName)}
                                            </Space>
                                        ) : (
                                            <Space size={6} wrap>
                                                <Space size={2}>
                                                    <Text delete type="secondary">
                                                        {previousName || 'N/A'}
                                                    </Text>
                                                    {renderYardDetailsPopover(entry.previousYardId, entry.previousYardName)}
                                                </Space>
                                                <SwapOutlined style={{ color: '#8c8c8c' }} />
                                                <Space size={2}>
                                                    <Text strong>{newName || 'N/A'}</Text>
                                                    {renderYardDetailsPopover(entry.newYardId, entry.newYardName)}
                                                </Space>
                                            </Space>
                                        )}
                                    </div>

                                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                        <Space size={4}>
                                            <ClockCircleOutlined />
                                            {when}
                                        </Space>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                        <Tooltip title={entry.changedByEmail || ''}>
                                            <Space size={4}>
                                                <UserOutlined />
                                                {actorName}
                                            </Space>
                                        </Tooltip>
                                    </div>

                                    {entry.comment ? (
                                        <Paragraph
                                            style={{
                                                margin: '6px 0 0',
                                                padding: '6px 10px',
                                                background: '#fafafa',
                                                borderRadius: 6,
                                                fontSize: 12
                                            }}
                                        >
                                            <Text type="secondary" style={{ fontSize: 11 }}>Reassignment comment: </Text>
                                            {entry.comment}
                                        </Paragraph>
                                    ) : null}

                                    {renderNotes(entry)}
                                </div>
                            )
                        };
                    })}
                />
            )}
        </Modal>
    );
};

export default YardReassignmentHistoryModal;
